import React from "react";
import { PAGE_NAME, ROUTE } from "../../../../constants/constants";
import { Breadcrumb } from "../../../atoms/breadcrumb";
import { Dropdown } from "../../../atoms/dropdown";
import { DropdownOptionsType, payloadType, ResponseType } from "../../../../types";
import { toast } from "react-toastify";
import { useLocation, useNavigate } from "react-router-dom";
import { useGetCloudBudgetQuery, useUpdateCloudBudgetMutation } from "../../../../store/cost/costSlice";
import { Textarea } from "../../../atoms/textarea";
import { FileUpload } from "../../../atoms/fileUpload";
import { Input } from "../../../atoms/input";
import { CustomDatePicker } from "../../../atoms/datePicker";

const EditCloudBudget = () => {
  const pageClassName = "p-editCloudBudget";
  const url = useLocation();

  const navigate = useNavigate();
  React.useEffect(() => {
    if (url?.state?.id === undefined) {
      navigate(ROUTE.CLOUD_BUDGET);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url]);

  const { data, isSuccess } = useGetCloudBudgetQuery(
    url?.state?.id as string,
    { skip: !url?.state?.id }
  );

  const [updateApi] = useUpdateCloudBudgetMutation();

  const initialPayload: payloadType = {
    cloud_infra: {
      value: "",
      error: "",
      isError: false,
    },
    name: {
      value: "",
      error: "",
      isError: false,
    },
    description: {
      value: "",
      error: "",
      isError: false,
    },
    start_date: {
      value: "",
      error: "",
      isError: false,
    },
    end_date: {
      value: "",
      error: "",
      isError: false,
    },
    monthly_cost: {
      value: "",
      error: "",
      isError: false,
    },
    annual_cost: {
      value: "",
      error: "",
      isError: false,
    },
    conversion_rate: {
      value: "",
      error: "",
      isError: false,
    },
    file: {
      value: "",
      error: "",
      isError: false,
    },
  };

  const [fileName, setFileName] = React.useState("");
  const [payload, setPayload] = React.useState(initialPayload);


  React.useEffect(() => {
    if (data?.result) {
      const budget = data?.result;

      setPayload({
        ...payload,
        cloud_infra: {
          ...payload.cloud_infra,
          value: {
            label: budget.saasTool,
            value: budget.saasTool,
          },
        },
        name: {
          ...payload.name,
          value: budget.name,
        },
        description: {
          ...payload.description,
          value: budget.description,
        },
        start_date: {
          ...payload.startDate,
          value: budget.startDate,
        },
        end_date: {
          ...payload.endDate,
          value: budget.endDate,
        },
        monthly_cost: {
          ...payload.monthlyCost,
          value: budget.monthlyCost,
        },
        annual_cost: {
          ...payload.annualCost,
          value: budget.annualCost,
        },
        conversion_rate: {
          ...payload.conversionRate,
          value: budget.conversionRate,
        },
      });

      setFileName(budget?.file?.file_name);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess, data]);

  const fileChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files === null) return;
    setPayload({
      ...payload,
      file: {
        ...payload.file,
        value: e.target.files[0] as File,
        isError: false,
        error: "",
      },
    });
    setFileName(e.target.files[0].name);
  };

  const submitHandler = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const formData = new FormData();
    formData.append("cloud_infra", (payload.cloud_infra.value as DropdownOptionsType)?.value as string);
    formData.append("name", payload.name.value as string);
    formData.append("description", payload.description.value as string);
    formData.append("start_date", payload.start_date.value as string);
    formData.append("end_date", payload.end_date.value as string);
    formData.append("monthly_cost", payload.monthly_cost.value as string);
    formData.append("annual_cost", payload.annual_cost.value as string);
    payload.conversion_rate.value && formData.append("conversion_rate", payload.conversion_rate.value as string)
    formData.append("file", payload.file.value as File);
    formData.append("currency", "USD");
    formData.append('_method', 'PUT');

    const response = await updateApi({ id: url?.state?.id as string, formData: formData }) as ResponseType;

    if (response?.data?.success) {
      toast.success(response?.data?.message);
      navigate(ROUTE.CLOUD_BUDGET);
    } else {
      if (response?.error?.data?.errors) {
        Object.keys(response?.error?.data?.errors).forEach((key) => {
          setPayload((prevState) => ({
            ...prevState,
            [key]: {
              ...prevState[key],
              error: response?.error?.data?.errors[key][0],
              isError: true,
            },
          }));
        });
      } else {
        toast.error(response?.error?.data?.message);
      }
    }
  };

  return (
    <div className={pageClassName}>
      <Breadcrumb />
      <p className={`${pageClassName}__title`}>{PAGE_NAME.EDIT_CLOUD_BUDGET}</p>
      <div className={`${pageClassName}__container`}>
        <form className={`${pageClassName}__form`} onSubmit={submitHandler}>
          <div className={`${pageClassName}__form__dropdown`}>
            <label className={`${pageClassName}__form__dropdown__label`}>
              Cloud Infra
            </label>
            <Dropdown
              options={[
                {
                  label: "GCP",
                  value: "GCP",
                },
                {
                  label: "AWS",
                  value: "AWS",
                },
                {
                  label: "Namecheap",
                  value: "Namecheap",
                },
              ]}
              onClickChange={(selected) => {
                setPayload({
                  ...payload,
                  cloud_infra: {
                    ...payload.cloud_infra,
                    value: selected as { label: string; value: string },
                    isError: false,
                    error: "",
                  },
                })
              }}
              placeholder="Please Select a Cloud Infra"
              isIcon
              color="black"
              isBordered
              selected={payload.cloud_infra.value as DropdownOptionsType}
              isError={payload.cloud_infra.isError}
              error={payload.cloud_infra.error}
            />
          </div>

          <Input
            placeholder="Enter Service Name"
            label="Service Name"
            onChange={(e) =>
              setPayload({
                ...payload,
                name: {
                  ...payload.name,
                  value: e.target.value,
                  isError: false,
                  error: "",
                },
              })
            }
            value={
              payload.name.value as string}
            isError={payload.name.isError}
            error={payload.name.error}
          />

          <Textarea
            placeholder="Enter Description"
            label="Description"
            onChange={(e) =>
              setPayload({
                ...payload,
                description: {
                  ...payload.description,
                  value: e.target.value,
                  isError: false,
                  error: "",
                },
              })
            }
            value={payload.description.value as string}
            isError={payload.description.isError}
            error={payload.description.error}
          />

          <div className={`${pageClassName}__form__dropdown`}>
            <label className={`${pageClassName}__form__dropdown__label`}>
              Start Date
            </label>
            <CustomDatePicker
              value={new Date(payload.start_date.value as string)}
              onChange={(date) => {
                setPayload({
                  ...payload,
                  start_date: {
                    ...payload.start_date,
                    value: date?.toString() ?? "",
                    isError: false,
                    error: "",
                  },
                });
              }}
              isError={payload.start_date.isError}
              error={payload.start_date.error}
            />
          </div>

          <div className={`${pageClassName}__form__dropdown`}>
            <label className={`${pageClassName}__form__dropdown__label`}>
              End Date
            </label>
            <CustomDatePicker
              value={new Date(payload.end_date.value as string)}
              onChange={(date) => {
                setPayload({
                  ...payload,
                  end_date: {
                    ...payload.end_date,
                    value: date?.toString() ?? "",
                    isError: false,
                    error: "",
                  },
                });
              }}
              isError={payload.end_date.isError}
              error={payload.end_date.error}
            />
          </div>

          <Input
            placeholder="Enter Monthly Cost"
            label="Monthly Cost"
            onChange={(e) =>
              setPayload({
                ...payload,
                monthly_cost: {
                  ...payload.monthly_cost,
                  value: e.target.value,
                  isError: false,
                  error: "",
                },
              })
            }
            value={payload.monthly_cost.value as string}
            isError={payload.monthly_cost.isError}
            error={payload.monthly_cost.error}
            type="number"
            readonly
          />

          <Input
            placeholder="Enter Annual Cost"
            label="Annual Cost"
            onChange={(e) =>
              setPayload({
                ...payload,
                annual_cost: {
                  ...payload.annual_cost,
                  value: e.target.value,
                  isError: false,
                  error: "",
                },
              })
            }
            value={payload.annual_cost.value as string}
            isError={payload.annual_cost.isError}
            error={payload.annual_cost.error}
            type="number"
          />

          <Input
            placeholder="Enter Conversion Rate"
            label="Conversion Rate"
            onChange={(e) =>
              setPayload({
                ...payload,
                conversion_rate: {
                  ...payload.conversion_rate,
                  value: e.target.value,
                  isError: false,
                  error: "",
                },
              })
            }
            value={payload.conversion_rate.value as string}
            isError={payload.conversion_rate.isError}
            error={payload.conversion_rate.error}
            type="number"
          />


          <FileUpload
            placeholder="Upload Invoice File"
            label="Invoice File"
            fileName={fileName}
            onChange={fileChangeHandler}
            isError={payload.file.isError}
            error={payload.file.error}
            accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
          />

          <div className={`${pageClassName}__action`}>
            <button
              className={`${pageClassName}__action__button`}
              type="submit"
            >
              Save
            </button>
            <button
              className={`${pageClassName}__action__button ${pageClassName}__action__cancel`}
              type="button"
              onClick={() => navigate(ROUTE.CLOUD_BUDGET)}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default EditCloudBudget;